import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { Zap } from "lucide-react"
import {
  fieldInputClass,
  fieldLabelClass,
  finalBulletClass,
  focusRing,
  formButtonClass,
  logEventClass,
  logTimeClass,
  timerClass,
} from "../lightTheme"
import { testDriveContent } from "./testDriveContent"

const previewCardShadow =
  "shadow-[0_0_0_1px_rgba(0,0,0,0.08),0_2px_2px_0_rgba(0,0,0,0.04),0_8px_16px_-4px_rgba(0,0,0,0.04),0_0_0_1px_#FAFAFA]"

const previewTabs = [
  { id: "lead", label: "Fills out the form", kind: "form" },
  { id: "assistant", label: "Connects with the assistant", kind: "log", count: 2, showFinal: false },
  { id: "call", label: "Books a test appointment", kind: "log", count: 3, showFinal: false },
  { id: "qualifying", label: "Qualifying", kind: "log", count: 3, showFinal: true },
]

function DriveField({ id, label, inputMode }) {
  return (
    <div className="flex w-full flex-col gap-3">
      <Label className={fieldLabelClass} htmlFor={id}>
        {label}
      </Label>
      <Input
        id={id}
        type="text"
        inputMode={inputMode}
        placeholder=""
        className={fieldInputClass}
      />
    </div>
  )
}

function LogLine({ time, event }) {
  return (
    <p className="text-[12px] leading-4">
      <span className={logTimeClass}>{time}</span>{" "}
      <span className={logEventClass}>{event}</span>
    </p>
  )
}

function CaptureForm() {
  return (
    <div className="flex flex-col gap-6 p-6">
      <DriveField
        id="direct-phone"
        label={testDriveContent.phoneLabel}
        inputMode="tel"
      />
      <DriveField
        id="property-zip"
        label={testDriveContent.zipLabel}
        inputMode="text"
      />
      <Button type="button" className={`${formButtonClass} gap-1.5`}>
        <Zap strokeWidth={1.5} className="text-white" aria-hidden="true" />
        {testDriveContent.cta}
      </Button>
    </div>
  )
}

export default function TestDriveSection() {
  const [activeTab, setActiveTab] = useState("lead")
  const tab = previewTabs.find((item) => item.id === activeTab) ?? previewTabs[0]
  const visibleLogs = testDriveContent.logs.slice(0, tab.count ?? 0)

  return (
    <section className="ml-[calc(50vw-50%-1.5rem)] grid w-full grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-8">
      <div className="flex flex-col gap-8 lg:col-span-6 lg:col-start-1">
        <h2 className="text-balance text-[40px] font-bold leading-[48px] tracking-[-0.04em] text-[#171717] max-md:text-[28px] max-md:leading-8">
          {testDriveContent.title}
        </h2>
        <p className="max-w-[64ch] text-[18px] font-normal leading-7 text-[#4D4D4D]">
          {testDriveContent.subtitle}
        </p>
      </div>
      <div className="flex min-w-0 flex-col gap-4 lg:col-span-6 lg:col-start-7">
        <div
          role="tablist"
          aria-label="Test drive"
          className="flex w-fit flex-wrap gap-1 self-start lg:grid lg:w-full lg:grid-cols-2"
        >
          {previewTabs.map((item) => {
            const selected = item.id === tab.id
            return (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={selected}
                onClick={() => setActiveTab(item.id)}
                className={cn(
                  "h-8 w-fit cursor-pointer whitespace-nowrap rounded-[6px] px-3.5 text-[14px] font-normal leading-5",
                  focusRing,
                  selected
                    ? "bg-[#EBEBEB] text-[#171717]"
                    : "text-[#4D4D4D] hover:text-[#171717]"
                )}
              >
                {item.label}
              </button>
            )
          })}
        </div>
        <div
          role="tabpanel"
          className={`flex min-h-[354px] w-full flex-col overflow-hidden rounded-xl bg-white ${previewCardShadow}`}
        >
          <div className="relative flex h-11 shrink-0 items-center px-4 shadow-[inset_0_-1px_0_rgba(0,0,0,0.08)]">
            <span className="flex items-center gap-1" aria-hidden="true">
              <span className="size-2 rounded-full bg-[#EE6D5E]" />
              <span className="size-2 rounded-full bg-[#F3BF4A]" />
              <span className="size-2 rounded-full bg-[#5DC753]" />
            </span>
            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 text-[13px] font-normal leading-5 text-[#7D7D7D]">
              {tab.label}
            </span>
          </div>
          {tab.kind === "form" ? (
            <CaptureForm />
          ) : (
            <div className="flex flex-col p-6">
              <p className={timerClass}>{testDriveContent.timer}</p>
              <div className="mt-6">
                {visibleLogs.map((line) => (
                  <LogLine key={line.time} time={line.time} event={line.event} />
                ))}
                {tab.showFinal ? (
                  <p className="text-[12px] leading-4">
                    <span className={finalBulletClass}>●</span>{" "}
                    <span className={logEventClass}>{testDriveContent.finalStatus}</span>
                  </p>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
